import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';

export type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'accent' | 'google';
export type ButtonSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-button',
  imports: [CommonModule],
  templateUrl: './button.component.html',
  styleUrl: './button.component.css'
})
export class ButtonComponent {
  @Input() variant: ButtonVariant = 'primary';
  @Input() size: ButtonSize = 'sm';
  @Input() disabled: boolean = false;
  @Input() loading: boolean = false;
  @Input() fullWidth: boolean = false;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() class: string = '';
  @Input() icon?: string; // SVG icon path or icon name
  @Input() iconPosition: 'left' | 'right' = 'left';

  @Output() clicked = new EventEmitter<MouseEvent>();

  onClick(event: MouseEvent): void {
    if (!this.disabled && !this.loading) {
      this.clicked.emit(event);
    }
  }

  get buttonClasses(): string {
    const classes = [
      // Base styles
      'inline-flex',
      'items-center',
      'justify-center',
      'gap-2',
      'font-medium',
      'rounded-lg',
      'transition-all',
      'duration-200',
      'outline-none',
      'relative',
      'select-none',
      'focus:outline-2',
      'focus:outline-offset-2',
      'focus:outline-blue-600'
    ];

    // Variant styles
    switch (this.variant) {
      case 'primary':
        classes.push('bg-blue-600', 'text-white', 'shadow-sm', 'hover:brightness-90', 'hover:shadow-md', 'focus:outline-blue-600', 'border-0');
        break;
      case 'secondary':
        classes.push('bg-white', 'border', 'border-gray-300', 'text-gray-800', 'shadow-sm', 'hover:bg-gray-50', 'hover:border-blue-600', 'dark:bg-gray-700', 'dark:border-gray-600', 'dark:text-gray-100');
        break;
      case 'success':
        classes.push('bg-green-500', 'text-white', 'shadow-sm', 'hover:brightness-90', 'hover:shadow-md', 'border-0');
        break;
      case 'danger':
        classes.push('bg-red-500', 'text-white', 'shadow-sm', 'hover:bg-red-600', 'hover:shadow-md', 'border-0');
        break;
      case 'warning':
        classes.push('bg-amber-500', 'text-white', 'shadow-sm', 'hover:brightness-90', 'hover:shadow-md', 'border-0');
        break;
      case 'accent':
        classes.push('bg-blue-500', 'text-white', 'shadow-sm', 'hover:brightness-90', 'hover:shadow-md', 'border-0');
        break;
      case 'google':
        classes.push('bg-white', 'text-gray-700', 'border', 'border-gray-300', 'shadow-sm', 'hover:bg-gray-50', 'dark:bg-gray-700', 'dark:border-gray-600', 'dark:text-gray-100');
        break;
    }

    // Size styles
    switch (this.size) {
      case 'sm':
        classes.push('px-4', 'py-2', 'text-sm');
        break;
      case 'md':
        classes.push('px-6', 'py-3', 'text-base');
        break;
      case 'lg':
        classes.push('px-8', 'py-4', 'text-lg');
        break;
    }

    // Full width
    if (this.fullWidth) {
      classes.push('w-full');
    }

    // Disabled state
    if (this.disabled || this.loading) {
      classes.push('opacity-60', 'cursor-not-allowed', 'pointer-events-none');
    } else {
      classes.push('cursor-pointer');
    }

    // Custom classes
    if (this.class) {
      classes.push(this.class);
    }

    return classes.join(' ');
  }
}

